import {type IDictionary} from '../../../@type/dictionary';
import {type FeatureFlag} from '../../../@type/features';
import {type IFeatureSupport} from '../../../@type/woocommerce/php-data';
import {DispatchActionType} from '../models/DispatchActionType';
import {type IEnvironment, type LoadingMode, type ModalPage} from '../models/IEnvironment';
import {type DispatchAction, store} from '../store';
import {getBaseURL, isDevEnvironment} from '../util/environment';
import {createDispatchUpdate} from './initialState';
import {MerchantConfiguration} from './merchantConfigurationReducer';

export function environmentReducer(state: IEnvironment, action: DispatchAction<unknown>): IEnvironment {
	switch (action.type) {
		case DispatchActionType.ENVIRONMENT:
			return {
				...(action as DispatchAction<IEnvironment>).payload,
				customer: {...(action as DispatchAction<IEnvironment>).payload.customer},
				plugin: {...(action as DispatchAction<IEnvironment>).payload.plugin},
				modalUI: {...(action as DispatchAction<IEnvironment>).payload.modalUI},
			};
		case DispatchActionType.ENVIRONMENT_LANGUAGE:
			return {
				...state,
				language: (action as DispatchAction<string>).payload,
			};
		case DispatchActionType.ENVIRONMENT_TRANSLATED_MODAL_TERMS:
			return {
				...state,
				translated_modal_terms: (action as DispatchAction<IDictionary>).payload,
			};
		case DispatchActionType.ENVIRONMENT_SET_FEATURES:
			return {
				...state,
				plugin: {
					...state.plugin,
					featureSupport: (action as DispatchAction<Record<string, IFeatureSupport | undefined>>).payload,
				},
			};
		default:
			return {
				...state,
				modalUI: {...state.modalUI},
			};
	}
}

// ======================= Action Creators =============================

export function updateEnvironment(options: {
	language?: string;
	translated_modal_terms?: IDictionary;
	customerIsMobile?: boolean;
	customerSaveToBrowserSelected?: boolean;
	pluginPageType?: 'product' | 'cart' | 'checkout' | 'shop';
	pluginIsTestMode?: boolean;
	pluginVersion?: string;
	pluginButtonColor?: string;
	modalButtonShadow?: boolean;
	modalIsOpen?: boolean;
	modalPageType?: ModalPage;
	modalLoading?: LoadingMode;
	requestedPage?: ModalPage | undefined;
}): DispatchAction<IEnvironment> {
	return {
		type: DispatchActionType.ENVIRONMENT,
		payload: {
			language: options.language ?? Environment.language(),
			translated_modal_terms: options.translated_modal_terms ?? Environment.translated_modal_terms(),
			customer: {
				mobile: options.customerIsMobile ?? Environment.customer.mobile(),
				saveToBrowserSelected: options.customerSaveToBrowserSelected ?? Environment.customer.saveToBrowserSelected(),
			},
			plugin: {
				version: options.pluginVersion ?? Environment.plugin.version(),
				mode: (typeof options.pluginIsTestMode === 'boolean') ? (options.pluginIsTestMode ? 'test' : 'live') : Environment.plugin.mode(),
				buttonColor: options.pluginButtonColor ?? Environment.plugin.buttonColor(),
				pageType: options.pluginPageType ?? Environment.plugin.pageType(),
				featureSupport: store.getState().environment.plugin.featureSupport,
			},
			modalUI: {
				open: options.modalIsOpen ?? Environment.modalUI.open(),
				page: (options.modalPageType) ?? Environment.modalUI.page(),
				loadingMode: options.modalLoading ?? Environment.modalUI.loadingMode(),
				requestedPage: options.requestedPage ?? Environment.modalUI.requestedPage(),
			},
		},
	};
}

export function setFeatureSupport(features: Record<string, IFeatureSupport | undefined> = {}): DispatchAction<Record<string, IFeatureSupport | undefined>> {
	return {
		type: DispatchActionType.ENVIRONMENT_SET_FEATURES,
		payload: features,
	};
}

export const updateLanguage = createDispatchUpdate<string>(DispatchActionType.ENVIRONMENT_LANGUAGE);
export const updateTranslatedModalTerms = createDispatchUpdate<IDictionary>(DispatchActionType.ENVIRONMENT_TRANSLATED_MODAL_TERMS);
export const startModalLoading = () => updateEnvironment({modalLoading: 'loading'});
export const startModalProcessing = () => updateEnvironment({modalLoading: 'processing'});
export const stopModalLoading = () => updateEnvironment({modalLoading: 'finished'});

// ======================= Store Selectors =============================

export const Environment = {
	environment: () => store.getState().environment,
	language: () => store.getState().environment.language,
	translated_modal_terms: () => store.getState().environment.translated_modal_terms,
	testMode: () => store.getState().environment.plugin.mode === 'test',
	apiURL: () => getBaseURL(MerchantConfiguration.hostName(), Environment.testMode()),
	isTestOrDevSite: () => isDevEnvironment(getBaseURL(MerchantConfiguration.hostName(), Environment.testMode())),
	customer: {
		mobile: () => store.getState().environment.customer.mobile,
		saveToBrowserSelected: () => store.getState().environment.customer.saveToBrowserSelected,
	},
	plugin: {
		version: () => store.getState().environment.plugin.version,
		mode: () => store.getState().environment.plugin.mode,
		buttonColor: () => store.getState().environment.plugin.buttonColor,
		pageType: () => store.getState().environment.plugin.pageType,
	},
	modalUI: {
		open: () => store.getState().environment.modalUI.open,
		page: () => store.getState().environment.modalUI.page,
		loadingMode: () => store.getState().environment.modalUI.loadingMode,
		requestedPage: () => store.getState().environment.modalUI.requestedPage,
	},
};

export const Feature = {
	enabled: (flag: FeatureFlag) => store.getState().environment.plugin.featureSupport[flag]?.enabled ?? false,
	// Version 0 means plugin does not supply a feature flag but was added for backwards compatibility
	version: (flag: FeatureFlag) => store.getState().environment.plugin.featureSupport[flag]?.version ?? 0,
	metadata: <T>(flag: FeatureFlag, key: string) => (store.getState().environment.plugin.featureSupport[flag]?.metadata?.[key] ?? null) as T | null,
	dynamicMetadata: <T>(flag: FeatureFlag, key: string, cartKey = '0') => (store.getState().calculatedCarts[cartKey]?.feature_metadata?.[flag]?.[key] ?? null) as T | null,
};
